import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

/**
 * Inline editable numeric cell — used on the products table for quick
 * price / stock edits without opening the full form dialog.
 *
 * Click (or focus + Enter) → transforms into an <input>.
 * Enter or blur saves via the parent-supplied onSave(value) which should
 * return a promise. Escape cancels.
 */
export default function InlineEditNumber({
    value,
    onSave,
    format,
    step = "1",
    min = 0,
    testId,
    className = "",
    align = "right",
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value ?? 0));
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { if (!editing) setDraft(String(value ?? 0)); }, [value, editing]);
    useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

    const commit = async () => {
        if (saving) return;
        const parsed = Number(draft);
        if (Number.isNaN(parsed) || parsed < min) {
            setDraft(String(value ?? 0));
            setEditing(false);
            return;
        }
        if (parsed === Number(value)) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(parsed);
            setEditing(false);
        } catch {
            setDraft(String(value ?? 0));
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => { setDraft(String(value ?? 0)); setEditing(false); };

    if (!editing) {
        return (
            <button
                type="button"
                data-testid={testId}
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                className={`w-full inline-block px-2 py-0.5 rounded font-mono text-${align} hover:bg-gold-100 focus:bg-gold-100 focus:outline-none focus:ring-1 focus:ring-gold-400 cursor-text ${className}`}
                title="点击编辑"
            >
                {format ? format(value) : value}
            </button>
        );
    }

    return (
        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
                ref={inputRef}
                data-testid={`${testId}-input`}
                type="number"
                step={step}
                min={min}
                value={draft}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                }}
                onBlur={() => commit()}
                className={`h-7 w-20 px-2 rounded border border-gold-400 font-mono text-${align} text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 disabled:opacity-60`}
            />
            {saving && <span className="text-[10px] text-gold-500">…</span>}
        </div>
    );
}
